'use client'

import { useEffect, useRef } from 'react'
import { ensurePostHogInit, withPostHog } from '@/lib/posthog/init'

/**
 * Root-layout mount. Drives Supabase auth-state into PostHog identify/reset.
 *
 * $pageview capture is handled by the SDK itself via
 * `capture_pageview: 'history_change'` (lib/posthog/init.ts) — no React
 * component needed; posthog-js intercepts history.pushState/popState and fires
 * pageview events automatically on App Router transitions.
 *
 * BOTH SDKs are deferred off the first-load chunk: posthog-js loads via
 * ensurePostHogInit()'s dynamic import, and the Supabase browser client is
 * dynamic-imported inside the effect — this component is what mounts on every
 * page (incl. static /terms, /privacy), so a static import of either would put
 * ~116 KB gz back into every page's critical path.
 *
 * Person-property writes live in <PostHogPersonProperties /> (own module, so
 * its useAccessState → useSession → supabase chain doesn't ride into the root
 * layout chunk); it mounts inside the authed shell.
 */
export function PostHogClient() {
  return <PostHogIdentify />
}

function PostHogIdentify() {
  const lastIdentifiedRef = useRef<string | null>(null)

  useEffect(() => {
    // Lazy init runs here (after hydration) instead of at module-eval to avoid
    // a script-injection hydration race — see ensurePostHogInit for details.
    void ensurePostHogInit()

    let cancelled = false
    let authSubscription: { unsubscribe: () => void } | undefined

    const syncIdentity = (user: { id: string; email?: string }) => {
      if (lastIdentifiedRef.current === user.id) return
      withPostHog((ph) => ph.identify(user.id, user.email ? { email: user.email } : undefined))
      lastIdentifiedRef.current = user.id
    }

    void (async () => {
      const { createClient } = await import('@/lib/supabase/client')
      if (cancelled) return
      const supabase = createClient()

      // Prime identity on mount — cookie session may already be present without
      // an auth-state event firing.
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (cancelled || !session?.user) return
        syncIdentity(session.user)
      })

      const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
        if (event === 'SIGNED_OUT' || !session?.user) {
          if (lastIdentifiedRef.current !== null) {
            withPostHog((ph) => ph.reset())
            lastIdentifiedRef.current = null
          }
          return
        }
        syncIdentity(session.user)
      })
      authSubscription = subscription
    })()

    return () => {
      cancelled = true
      authSubscription?.unsubscribe()
    }
  }, [])

  return null
}
